import { computed, ref, shallowRef } from 'vue'
import type { TableChangeExtra, TableColumnType, TableFilters, TableKey, TablePaginationConfig, TableSorter, TableSortOrder } from './types'

export interface TableDataProps<T> {
  columns: TableColumnType<T>[]
  dataSource: T[]
  rowKey: string | ((record: T) => TableKey)
  childrenColumnName: string
  pagination: false | TablePaginationConfig
}

export function columnKey<T>(column: TableColumnType<T>, index: number): string {
  return String(column.key ?? (Array.isArray(column.dataIndex) ? column.dataIndex.join('.') : column.dataIndex) ?? index)
}

export function cellValue<T>(record: T, column: TableColumnType<T>): unknown {
  const path = Array.isArray(column.dataIndex) ? column.dataIndex : column.dataIndex === undefined ? [] : [column.dataIndex]
  return path.reduce<unknown>((value, key) => value == null ? undefined : (value as Record<string | number, unknown>)[key], record)
}

export function useTableData<T extends Record<string, any>>(
  props: TableDataProps<T>,
  onChange: (pagination: TablePaginationConfig, filters: TableFilters, sorter: TableSorter<T>, extra: TableChangeExtra<T>) => void
) {
  const paginationConfig = computed(() => props.pagination === false ? {} : props.pagination)
  const innerCurrent = ref(paginationConfig.value.defaultCurrent ?? 1)
  const innerPageSize = ref(paginationConfig.value.defaultPageSize ?? 10)
  const defaultColumn = props.columns.find(column => column.defaultSortOrder)
  const innerSorter = shallowRef<TableSorter<T>>(defaultColumn ? sorterFor(defaultColumn, defaultColumn.defaultSortOrder ?? null) : {})
  const innerFilters = ref<TableFilters>(Object.fromEntries(props.columns.filter(column => column.defaultFilteredValue).map((column, index) => [columnKey(column, index), column.defaultFilteredValue ?? null])))
  const filters = computed<TableFilters>(() => Object.fromEntries(props.columns.map((column, index) => [columnKey(column, index), column.filteredValue !== undefined ? column.filteredValue : innerFilters.value[columnKey(column, index)] ?? null]).filter(([key]) => props.columns.some((column, index) => columnKey(column, index) === key && (column.filters || column.onFilter || column.filteredValue !== undefined)))))
  const sorter = computed<TableSorter<T>>(() => {
    const controlled = props.columns.filter(column => column.sortOrder !== undefined)
    if (!controlled.length) return innerSorter.value
    const active = controlled.find(column => column.sortOrder)
    return active ? sorterFor(active, active.sortOrder ?? null) : {}
  })
  const processedRecords = computed(() => processRecords(props.dataSource, sorter.value, filters.value))
  const pageSize = computed(() => paginationConfig.value.pageSize ?? innerPageSize.value)
  const total = computed(() => paginationConfig.value.total ?? processedRecords.value.length)
  const current = computed(() => Math.max(1, Math.min(paginationConfig.value.current ?? innerCurrent.value, Math.ceil(total.value / pageSize.value) || 1)))
  const pagination = computed<TablePaginationConfig>(() => props.pagination === false ? {} : { ...paginationConfig.value, current: current.value, pageSize: pageSize.value, total: total.value })
  const pageRecords = computed(() => {
    if (props.pagination === false) return processedRecords.value
    // Server-paged records are already sliced; local full datasets are paginated here.
    if (total.value > processedRecords.value.length && processedRecords.value.length <= pageSize.value) return processedRecords.value
    return processedRecords.value.slice((current.value - 1) * pageSize.value, current.value * pageSize.value)
  })
  function recordKey(record: T): TableKey {
    const value = typeof props.rowKey === 'function' ? props.rowKey(record) : record[props.rowKey]
    if (typeof value !== 'string' && typeof value !== 'number') throw new Error('RrTable: each record needs a string or number rowKey')
    return value
  }
  function childrenOf(record: T): T[] {
    const value = record[props.childrenColumnName]
    return Array.isArray(value) ? value : []
  }
  function indexRecords(records: T[]) {
    const result = new Map<TableKey, T>()
    function visit(items: T[]) {
      for (const item of items) {
        const key = recordKey(item)
        if (result.has(key)) throw new Error(`RrTable: duplicate rowKey ${key}`)
        result.set(key, item)
        visit(childrenOf(item))
      }
    }
    visit(records)
    return result
  }
  const recordIndex = computed(() => indexRecords(props.dataSource))
  function sorterFor(column: TableColumnType<T>, order: TableSortOrder): TableSorter<T> {
    return { column, columnKey: column.key ?? columnKey(column, props.columns.indexOf(column)), field: column.dataIndex, order }
  }
  function processRecords(records: T[], sorting: TableSorter<T>, filtering: TableFilters): T[] {
    const filtered = records.filter(record => props.columns.every((column, index) => {
      const values = filtering[columnKey(column, index)]
      return !column.onFilter || !values?.length || values.some(value => column.onFilter?.(value, record))
    }))
    if (sorting.order && typeof sorting.column?.sorter === 'function') {
      const compare = sorting.column.sorter
      filtered.sort((left, right) => compare(left, right, sorting.order) * (sorting.order === 'ascend' ? 1 : -1))
    }
    return filtered.map(record => childrenOf(record).length ? { ...record, [props.childrenColumnName]: processRecords(childrenOf(record), sorting, filtering) } : record)
  }
  function changeSort(column: TableColumnType<T>) {
    if (!column.sorter) return
    const directions = column.sortDirections ?? ['ascend', 'descend']
    const currentOrder = sorter.value.columnKey === (column.key ?? columnKey(column, props.columns.indexOf(column))) ? sorter.value.order : null
    const nextOrder = directions[directions.indexOf(currentOrder as Exclude<TableSortOrder, null>) + 1] ?? null
    const next = sorterFor(column, nextOrder)
    innerSorter.value = next
    onChange(pagination.value, filters.value, next, { action: 'sort', currentDataSource: processRecords(props.dataSource, next, filters.value) })
  }
  function changeFilter(column: TableColumnType<T>, values: TableKey[]) {
    const next = { ...filters.value, [columnKey(column, props.columns.indexOf(column))]: values.length ? values : null }
    innerFilters.value = next
    innerCurrent.value = 1
    const data = processRecords(props.dataSource, sorter.value, next)
    onChange(props.pagination === false ? {} : { ...pagination.value, current: 1 }, next, sorter.value, { action: 'filter', currentDataSource: data })
  }
  function changePage(page: number, size: number) {
    if (props.pagination === false) return
    if (pageSize.value !== size) paginationConfig.value.onShowSizeChange?.(page, size)
    innerCurrent.value = page
    innerPageSize.value = size
    paginationConfig.value.onChange?.(page, size)
    onChange({ ...pagination.value, current: page, pageSize: size }, filters.value, sorter.value, { action: 'paginate', currentDataSource: processedRecords.value })
  }
  return { recordKey, childrenOf, recordIndex, pageRecords, processedRecords, pagination, filters, sorter, changeSort, changeFilter, changePage }
}
